import type { Service } from '../../graphqlEnums'
import type { PostInputMetaData } from '../graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import type { MapperContext } from '../types'

// Maps a per-channel `Draft` (+ save context) to one populated key on
// `PostInputMetaData` (the Google Business key is `google`, not
// `googlebusiness`).
//
// Fields a user can REMOVE on edit are emitted via `clearable(value, ctx)` (see
// `shared/clearable.ts`) so the removal is sent as an explicit `null` — native
// `editPost` preserves an omitted sub-field, so an absent value would otherwise
// leave the stored value untouched. Fields that are always set to a concrete
// value (required fields, or a fixed enum such as the post `type`) stay
// omit-when-absent. When you add a clearable field, add an edit-clear test
// (see CORE-6430).
export type MetadataMapper = (
  draft: Draft,
  ctx: MapperContext,
) => PostInputMetaData

// One service's registration: its metadata mapper.
export type ServiceMapperEntry = {
  mapper: MetadataMapper
}

// Thrown by not-yet-implemented channel mappers so an unhandled channel fails
// loudly rather than silently dropping data.
export class NotImplemented extends Error {
  constructor(mapper: string) {
    super(`[composer/save] metadata mapper not implemented: ${mapper}`)
    this.name = 'NotImplemented'
  }
}

// The registration for a service without a real mapper: a mapper that throws
// NotImplemented. Add this for new channels before writing the real mapper.
export const notImplementedEntry = (service: Service): ServiceMapperEntry => ({
  mapper: (_draft: Draft, _ctx: MapperContext): never => {
    throw new NotImplemented(`map ${service}`)
  },
})
